import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import FavoritesController from '@jwp/ott-common/src/controllers/FavoritesController';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { useProfileStore } from '@jwp/ott-common/src/stores/ProfileStore';
import { ACCESS_MODEL } from '@jwp/ott-common/src/constants';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useProfiles } from '@jwp/ott-hooks-react/src/useProfiles';
import AccountIcon from '@jwp/ott-theme/assets/icons/People.svg?react';
import SettingsIcon from '@jwp/ott-theme/assets/icons/Settings.svg?react';
import SubscriptionIcon from '@jwp/ott-theme/assets/icons/Subscription.svg?react';
import SignoutIcon from '@jwp/ott-theme/assets/icons/Signout.svg?react';
import TermsIcon from '@jwp/ott-theme/assets/icons/Terms.svg?react';
import ExportData from '@jwp/ott-theme/assets/icons/exportdata.svg?react';
import Privacypolicy from '@jwp/ott-theme/assets/icons/Privacypolicy.svg?react';
import {
  RELATIVE_PATH_USER_ACCOUNT,
  RELATIVE_PATH_USER_FAVORITES,
  RELATIVE_PATH_USER_MY_PROFILE,
  RELATIVE_PATH_USER_PAYMENTS,
  RELATIVE_PATH_USER_SETTINGS,
  RELATIVE_PATH_USER_LEGAL,
  RELATIVE_PATH_USER_PRIVACY_POLICY,
  RELATIVE_PATH_USER_TERMS_OF_USE,
  RELATIVE_PATH_USER_SIGN_OUT,
} from '@jwp/ott-common/src/paths';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';
import { cognitoLogout } from '@jwp/ott-common/src/services/integrations/CustomAccountService';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useAppInfo } from '@jwp/ott-hooks-react/src/useAppInfo';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';

import { APP_RELEASE_VERSION } from '../../../../../platforms/web/src/config/clientEnvConfig';
import AccountComponent from '../../components/Account/Account';
import Button from '../../components/Button/Button';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import Favorites from '../../components/Favorites/Favorites';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import PaymentContainer from '../../containers/PaymentContainer/PaymentContainer';
import EditProfile from '../../containers/Profiles/EditProfile';
import Icon from '../../components/Icon/Icon';
import Signout from '../../components/Signout/Signout';
import Legal from '../../components/Legal/Legal';
import TermsOfUse from '../../components/TermsOfUse/TermsOfUse';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';
import Settings from '../../components/Settings/Settings';
import { modalURLFromLocation } from '../../utils/location';
import { useTheme } from '../../../../../ThemeContext';

import styles from './User.module.scss';

const User = (): JSX.Element => {
  const favoritesController = getModule(FavoritesController);
  const accountController = getModule(AccountController);

  const { accessModel, favoritesList } = useConfigStore(
    (s) => ({
      accessModel: s.accessModel,
      favoritesList: s.config.features?.favoritesList,
    }),
    shallow,
  );
  const navigate = useNavigate();
  const { t } = useTranslation('user');
  const breakpoint = useBreakpoint();
  const [clearFavoritesOpen, setClearFavoritesOpen] = useState(false);
  const location = useLocation();
  const { universalStyles } = useTheme();

  const {
    isCognitoEnabled,
    canShowTermsOfUse,
    canShowPrivacyPolicy,
    canShowLegal,
    canShowExportData,
    isSupportEmail,
    isFavoriteEnable,
    canShowSettings,
    isSubscriptionEnabled,
    isAuthenticationEnabled,
    isGuestModeEnabled,
    guestStreamingEnabled,
    isQrAuthenticationEnabled,
    privacyPolicyPlacement,
    termsOfUsePlacement,
  } = useAppConfig();
  const { redirectionPolicyURL, redirectionTosURL } = useUIControls();
  const { supportEmail, termsOfUse, privacyPolicy, supportEmailType, supportLabel } = useAppInfo();
  const helpLinkText = supportLabel || supportEmail;

  const appVersion = APP_RELEASE_VERSION;

  const shouldShowProfileForGuestMode = !isAuthenticationEnabled && isGuestModeEnabled && guestStreamingEnabled && !isQrAuthenticationEnabled;
  const shouldShowTermsOfUseButton =
    !canShowLegal && canShowTermsOfUse && termsOfUsePlacement?.includes('settings') && (termsOfUse?.en || termsOfUse?.es);
  const shouldShowPrivacyPolicyButton =
    !canShowLegal && canShowPrivacyPolicy && privacyPolicyPlacement?.includes('settings') && (privacyPolicy?.en || privacyPolicy?.es);
  const isLargeScreen = breakpoint > Breakpoint.md;
  const { user: customer, subscription, loading } = useAccountStore();
  const { canUpdateEmail } = accountController.getFeatures();
  const { profile } = useProfileStore();
  const favorites = useFavoritesStore((state) => state.getPlaylist());

  const { profilesEnabled } = useProfiles();

  const profileAndFavoritesPage = location.pathname?.includes('my-profile') || location.pathname.includes('favorites');

  const onLogout = useCallback(async () => {
    // Empty customer on a user page leads to navigate (code bellow), so we don't repeat it here
    if (isCognitoEnabled) {
      cognitoLogout();
    }
    if (!isCognitoEnabled) {
      await accountController.logout();
    }
  }, [accountController]);

  useEffect(() => {
    if (!loading && !customer && !shouldShowProfileForGuestMode) {
      navigate('/', { replace: true });
    }
  }, [navigate, customer, loading]);

  useEffect(() => {
    const handlePopState = () => {
      navigate('/', { replace: true });
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  if (!customer && !shouldShowProfileForGuestMode) {
    return (
      <div className={styles.user}>
        <LoadingOverlay inline />
      </div>
    );
  }

  return (
    <div className={styles.user}>
      {isLargeScreen && (
        <nav className={styles.leftColumn}>
          <div className={styles.leftpanel}>
            <ul>
              {!shouldShowProfileForGuestMode && (
                <li>
                  <Button
                    to={RELATIVE_PATH_USER_ACCOUNT}
                    label={t('nav.account')}
                    variant="navBar"
                    startIcon={<Icon icon={AccountIcon} />}
                    className={styles.button}
                    size="exlarge"
                    textStyle={universalStyles.leftSideBarText}
                  />
                </li>
              )}
              {isSubscriptionEnabled && (
                <li>
                  <Button
                    to={RELATIVE_PATH_USER_PAYMENTS}
                    label={t('nav.my_subscription')}
                    variant="navBar"
                    startIcon={<Icon icon={SubscriptionIcon} />}
                    className={styles.button}
                    size="exlarge"
                    textStyle={universalStyles.leftSideBarText}
                  />
                </li>
              )}
              {canShowExportData ? (
                <li>
                  <Button
                    label={t('nav.export_data')}
                    variant="navBarExport"
                    startIcon={<Icon icon={ExportData} />}
                    className={styles.button}
                    size="exlarge"
                    textStyle={universalStyles.leftSideBarText}
                    onClick={() => navigate(modalURLFromLocation(location, 'export-data'), { replace: true })}
                  />
                </li>
              ) : null}

              {shouldShowTermsOfUseButton ? (
                <li>
                  <Button
                    to={redirectionTosURL ? redirectionTosURL : RELATIVE_PATH_USER_TERMS_OF_USE}
                    {...(redirectionTosURL ? { target: '_blank', rel: 'noreferrer' } : {})}
                    label={t('nav.terms_of_use')}
                    variant="navBar"
                    startIcon={<Icon icon={TermsIcon} />}
                    className={styles.button}
                    size="exlarge"
                    textStyle={universalStyles.leftSideBarText}
                  />
                </li>
              ) : null}

              {shouldShowPrivacyPolicyButton ? (
                <li>
                  <Button
                    to={redirectionPolicyURL ? redirectionPolicyURL : RELATIVE_PATH_USER_PRIVACY_POLICY}
                    {...(redirectionPolicyURL ? { target: '_blank', rel: 'noreferrer' } : {})}
                    label={t('nav.privacy_policy')}
                    variant="navBar"
                    startIcon={<Icon icon={Privacypolicy} />}
                    className={styles.button}
                    size="exlarge"
                    textStyle={universalStyles.leftSideBarText}
                  />
                </li>
              ) : null}

              {canShowSettings && (
                <li>
                  <Button
                    to={RELATIVE_PATH_USER_SETTINGS}
                    label={t('nav.settings')}
                    variant="navBar"
                    startIcon={<Icon icon={SettingsIcon} />}
                    className={styles.button}
                    size="exlarge"
                    textStyle={universalStyles.leftSideBarText}
                  />
                </li>
              )}
              {canShowLegal && (
                <li>
                  <Button
                    to={RELATIVE_PATH_USER_LEGAL}
                    label={t('nav.legal')}
                    variant="navBar"
                    startIcon={<Icon icon={TermsIcon} />}
                    className={styles.button}
                    size="exlarge"
                    textStyle={universalStyles.leftSideBarText}
                  />
                </li>
              )}
              {!shouldShowProfileForGuestMode && (
                <li className={styles.logoutLi}>
                  <Button
                    to={RELATIVE_PATH_USER_SIGN_OUT}
                    label={t('nav.signout')}
                    variant="navBar"
                    startIcon={<Icon icon={SignoutIcon} />}
                    className={styles.button}
                    size="exlarge"
                    textStyle={universalStyles.leftSideBarText}
                  />
                </li>
              )}
            </ul>
            <hr style={universalStyles.divider} />
            <div className={styles.version} style={universalStyles.footerText}>
              Version {appVersion}
            </div>
            {isSupportEmail && supportEmail ? (
              <div style={universalStyles.footerText}>
                <p className={styles.helpSupport} style={universalStyles.helpValueText}>
                  <span className={styles.helpSupport} style={universalStyles.helpText}>
                    {t('nav.help')}:
                  </span>{' '}
                  {supportEmailType === 'email' ? (
                    <a href={`mailto:${supportEmail}`} className={styles.helpSupport} style={universalStyles.helpValueText}>
                      {helpLinkText}
                    </a>
                  ) : supportEmailType === 'web' ? (
                    <a href={supportEmail} target="_blank" rel="noopener noreferrer" className={styles.helpSupport} style={universalStyles.helpValueText}>
                      {helpLinkText}
                    </a>
                  ) : (
                    <span className={styles.helpSupport} style={universalStyles.helpValueText}>
                      {helpLinkText}
                    </span>
                  )}
                </p>
              </div>
            ) : null}

            <br />
          </div>
        </nav>
      )}
      <div className={styles.mainColumn}>
        <Routes>
          <Route
            path={RELATIVE_PATH_USER_ACCOUNT}
            element={<AccountComponent panelClassName={styles.panel} panelHeaderClassName={styles.panelHeader} canUpdateEmail={canUpdateEmail} />}
          />
          {profilesEnabled && <Route path={RELATIVE_PATH_USER_MY_PROFILE} element={<EditProfile contained />} />}
          {isFavoriteEnable && favoritesList && (
            <Route
              path={RELATIVE_PATH_USER_FAVORITES}
              element={
                <>
                  <Favorites
                    playlist={favorites}
                    onClearFavoritesClick={() => setClearFavoritesOpen(true)}
                    accessModel={accessModel}
                    hasSubscription={!!subscription}
                  />
                  <ConfirmationDialog
                    open={clearFavoritesOpen}
                    title={t('user:favorites.clear_favorites_title')}
                    body={t('user:favorites.clear_favorites_body')}
                    onConfirm={async () => {
                      await favoritesController.clear();
                      setClearFavoritesOpen(false);
                    }}
                    onClose={() => setClearFavoritesOpen(false)}
                  />
                </>
              }
            />
          )}
          {isSubscriptionEnabled && (
            <Route
              path={RELATIVE_PATH_USER_PAYMENTS}
              element={accessModel !== ACCESS_MODEL.AVOD ? <PaymentContainer /> : <Navigate to={RELATIVE_PATH_USER_ACCOUNT} />}
            />
          )}
          {canShowTermsOfUse && (
            <Route path={RELATIVE_PATH_USER_TERMS_OF_USE} element={<TermsOfUse panelClassName={styles.panel} panelHeaderClassName={styles.panelHeader} />} />
          )}
          {canShowPrivacyPolicy && (
            <Route
              path={RELATIVE_PATH_USER_PRIVACY_POLICY}
              element={<PrivacyPolicy panelClassName={styles.panel} panelHeaderClassName={styles.panelHeader} />}
            />
          )}
          {canShowSettings && (
            <Route path={RELATIVE_PATH_USER_SETTINGS} element={<Settings panelClassName={styles.panel} panelHeaderClassName={styles.panelHeader} />} />
          )}
          {canShowLegal && (
            <Route path={RELATIVE_PATH_USER_LEGAL} element={<Legal panelClassName={styles.panel} panelHeaderClassName={styles.panelHeader} />} />
          )}
          <Route
            path={RELATIVE_PATH_USER_SIGN_OUT}
            element={
              <Signout logoutYes={onLogout} cancelLogout={RELATIVE_PATH_USER_ACCOUNT} panelClassName={styles.panel} panelHeaderClassName={styles.panelHeader} />
            }
          />
          <Route path="*" element={<Navigate to={RELATIVE_PATH_USER_ACCOUNT} />} />
        </Routes>
      </div>
    </div>
  );
};

export default User;
